package org.braunequip.web.components.sections.AdminTables

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.icons.fa.FaEraser
import com.varabyte.kobweb.silk.components.icons.fa.FaPencil
import com.varabyte.kobweb.silk.components.icons.fa.FaRotate
import com.varabyte.kobweb.silk.components.icons.fa.FaTrash
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import kotlinx.coroutines.launch
import org.braunequip.web.components.forms.AdminEmployeePinForm
import org.braunequip.web.components.models.PinDTO
import org.braunequip.web.components.widgets.ConfirmModal
import org.braunequip.web.components.widgets.loadingSpinner
import org.braunequip.web.styles.AdminStyles.*
import org.braunequip.web.util.*
import org.braunequip.web.util.Constants.FONT_FAMILY2
import org.braunequip.web.util.Constants.SHARED.ROWS_PER_REQ
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.*

data class AdminPinsTableUiState(
    var showLoading: Boolean = true,
    var updatePopup: Boolean = false,
    var updatePinDTO: PinDTO? = null,

    var createPopup: Boolean = false,

    var deletePopup: Boolean = false,
)


@Composable
fun AdminEmployeePinsTable() {
    val breakpoint = rememberBreakpoint()
    val scope = rememberCoroutineScope()

    val pinsList = remember { mutableStateListOf<PinDTO>() }

    var rowsToSkip by remember { mutableStateOf(0) }
    var showMoreVisibility by remember { mutableStateOf(false) }

    val selectedItems = remember { mutableStateListOf<String>() }
    var selectedText by remember { mutableStateOf("0 Items Selected") }

    var uiState by remember { mutableStateOf(AdminPinsTableUiState()) }

    if (
        uiState.updatePopup
    ) {
        AdminEmployeePinForm(
            toUpdatePin = uiState.updatePinDTO,
            onSubmit = {
                scope.launch {
                    updateEmployeePin(it)
                    uiState = uiState.copy(
                        updatePopup = false,
                        updatePinDTO = null
                    )

                    rowsToSkip = 0
                    fetchEmployeePins(
                        skip = rowsToSkip,
                        onSuccess = {
                            pinsList.clear()
                            pinsList.addAll(it)
                            rowsToSkip += ROWS_PER_REQ
                            showMoreVisibility = it.size >= ROWS_PER_REQ
                        },
                        onError = {
                            console.log(it)
                        }
                    )
                }
            },
            onDismiss = {
                uiState = uiState.copy(updatePopup = false)
            }
        )
    }

    if (
        uiState.createPopup
    ) {
        AdminEmployeePinForm(
            toUpdatePin = uiState.updatePinDTO,
            onSubmit = {
                scope.launch {
                    createEmployeePin(it) {}
                    uiState = uiState.copy(
                        createPopup = false,
                        updatePinDTO = null
                    )

                    rowsToSkip = 0
                    fetchEmployeePins(
                        skip = rowsToSkip,
                        onSuccess = {
                            pinsList.clear()
                            pinsList.addAll(it)
                            rowsToSkip += ROWS_PER_REQ
                            showMoreVisibility = it.size >= ROWS_PER_REQ
                        },
                        onError = {
                            console.log(it)
                        }
                    )

                }
            },
            onDismiss = {
                uiState = uiState.copy(createPopup = false)
            }
        )
    }

    if (
        uiState.deletePopup
    ) {
        ConfirmModal(
            actionText = "Delete ${selectedItems.size} Pin(s)",
            onConfirm = {
                scope.launch {
                    val result = deleteSelectedEmployeePins(ids = selectedItems)
                    if (result) {
                        rowsToSkip -= selectedItems.size

                        selectedItems.forEach { deletedPostId ->
                            pinsList.removeAll {
                                it.id.toString() == deletedPostId
                            }
                        }

                        uiState = uiState.copy(showLoading = false)
                        selectedItems.clear()
                        selectedText = parseSelectedItemsTextByList(selectedItems)
                    }
                }
                uiState = uiState.copy(
                    deletePopup = false,
                    showLoading = false
                )
            },
            onDismiss = {
                uiState = uiState.copy(
                    deletePopup = false,
                    showLoading = false
                )
            }
        )
    }

    LaunchedEffect(Unit) {
        fetchEmployeePins(
            skip = rowsToSkip,
            onSuccess = {
                uiState = uiState.copy(showLoading = false)
                pinsList.clear()
                pinsList.addAll(it)
                rowsToSkip += ROWS_PER_REQ
                showMoreVisibility = it.size >= ROWS_PER_REQ
            },
            onError = {
                console.log(it)
            }
        )
    }

    Row(
        modifier = Modifier
            .padding(6.px)
            .fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically
    ) {
        SpanText(
            text = selectedText,
            modifier = Modifier
                .fontSize(18.px)
                .fontFamily(FONT_FAMILY2)
                .fontWeight(FontWeight.Light)
                .color(if (selectedItems.size > 0) Colors.Black else Theme.HalfBlack.rgb)
        )

        SpanText(
            text = "${pinsList.size} Total Pins",
            modifier = Modifier
                .fontSize(18.px)
                .fontFamily(FONT_FAMILY2)
                .fontWeight(FontWeight.Light)
        )
    }

    Row(
        modifier = Modifier
            .padding(6.px)
            .fillMaxWidth()
            .gap(15.px),
        horizontalArrangement = Arrangement.Start,
        verticalAlignment = Alignment.CenterVertically
    ) {
        Button(
            attrs = Modifier
                .classNames("button")
                .backgroundColor(Colors.LightGray)
                .color(Colors.White)
                .disabled(selectedItems.isEmpty())
                .onClick {
                    scope.launch {
                        selectedItems.clear()
                        selectedText = "0 Items Selected"
                    }
                }
                .toAttrs()
        ) {
            if (breakpoint < Breakpoint.MD) {
                FaEraser()
            } else {
                Text("Clear")
            }
        }

        Button(
            attrs = Modifier
                .classNames("button", "is-danger")
                .color(Colors.White)
                .disabled(selectedItems.isEmpty())
                .onClick {
                    uiState = uiState.copy(
                        deletePopup = true,
                        showLoading = true
                    )
                }
                .toAttrs()
        ) {
            if (breakpoint < Breakpoint.MD) {
                FaTrash()
            } else {
                Text("Delete")
            }
        }

        Button(
            attrs = Modifier
                .classNames("button")
                .backgroundColor(Theme.Primary.rgb)
                .color(Colors.White)
                .onClick {
                    scope.launch {
                        uiState = uiState.copy(showLoading = true)
                        rowsToSkip = 0

                        fetchEmployeePins(
                            skip = rowsToSkip,
                            onSuccess = {
                                uiState = uiState.copy(showLoading = false)
                                pinsList.clear()
                                pinsList.addAll(it)
                                rowsToSkip = ROWS_PER_REQ
                                showMoreVisibility = it.size >= ROWS_PER_REQ
                            },
                            onError = {
                                console.log(it)
                            }
                        )
                    }
                }
                .toAttrs()
        ) {
            if (breakpoint < Breakpoint.MD) {
                FaRotate()
            } else {
                Text("Refresh")
            }
        }

        Button(
            attrs = Modifier
                .classNames("button")
                .backgroundColor(Theme.Primary.rgb)
                .color(Colors.White)
                .onClick {
                    scope.launch {
                        uiState = uiState.copy(
                            createPopup = true,
                            updatePinDTO = null
                        )
                    }
                }
                .toAttrs()
        ) {
            if (breakpoint < Breakpoint.MD) {
                FaPencil()
            } else {
                Text("Create")
            }
        }

        if (uiState.showLoading) {
            loadingSpinner()
        }
    }

    ViewPins(
        pins = pinsList,
        selectedItems = selectedItems,
        onSelect = { id ->
            selectedItems.add(id)
            selectedText = parseSelectedItemsTextByList(selectedItems)
        },
        onDeselect = { id ->
            selectedItems.remove(id)
            selectedText = parseSelectedItemsTextByList(selectedItems)
        },
        onEdit = {
            uiState = uiState.copy(
                updatePopup = true,
                updatePinDTO = it
            )
        },
        showMoreVisibility = showMoreVisibility,
        onShowMore = {
            scope.launch {
                fetchEmployeePins(
                    skip = rowsToSkip,
                    onSuccess = {
                        if (it.isNotEmpty()) {
                            pinsList.addAll(it)
                            rowsToSkip += ROWS_PER_REQ
                            showMoreVisibility = it.size >= ROWS_PER_REQ
                            if (it.size < ROWS_PER_REQ) {
                                showMoreVisibility = false
                            }
                        }  else {
                            showMoreVisibility = false
                        }
                    },
                    onError = {
                        println(it)
                    }
                )
            }
        },
    )

}

@Composable
fun ViewPins(
    pins: List<PinDTO>,
    selectedItems: List<String>,
    onSelect: (String) -> Unit,
    onDeselect: (String) -> Unit,
    onEdit: (PinDTO) -> Unit,
    showMoreVisibility: Boolean,
    onShowMore: () -> Unit,
) {
    Column(
        modifier = Modifier
            .fillMaxWidth() // Ensure it takes full width
            .maxWidth(100.percent) // Constrain width to prevent infinite expansion
            .overflow(Overflow.Auto)
            .border(1.px, LineStyle.Solid, Colors.Grey)
            .backgroundColor(Colors.White),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {

        Box(
            modifier = Modifier
                .overflow(Overflow.Auto) // Enables horizontal scrolling
                .width(100.percent) // Ensures the container does not exceed screen width
        ) {
            Table(
                attrs = Modifier
                    .minWidth(500.px)
                    .fillMaxSize()
                    .classNames("table")
                    .margin(topBottom = 0.px)
                    .padding(topBottom = 0.px)
                    .toAttrs()
            ) {
                Thead(Modifier
                    .toAttrs {
                        style {
                            property("table-layout", "fixed")
                        }
                    }
                ) {
                    Tr {
                        Th(
                            TableHeaderStyle.toModifier()
                            .then(SmallColumnStyle).toAttrs()) {
                            Text("Select")
                        }
                        Th(
                            TableHeaderStyle.toModifier()
                            .then(SmallColumnStyle).toAttrs()) {
                            Text("ID")
                        }
                        Th(
                            TableHeaderStyle.toModifier()
                                .then(MediumColumnStyle).toAttrs()) {
                            Text("PIN")
                        }
                        Th(
                            TableHeaderStyle.toModifier()
                                .then(LargeColumnStyle)
                                .toAttrs()
                        ) {
                            Text("Description")
                        }
                        Th(
                            TableHeaderStyle.toModifier()
                            .then(MediumColumnStyle).toAttrs()) {
                            Text("Created")
                        }
                    }
                }
                Tbody {
                    pins.forEach { row ->
                        PinPreview(
                            pin = row,
                            selectedItems = selectedItems,
                            onSelect = onSelect,
                            onDeselect = onDeselect,
                            onEdit = onEdit
                        )
                    }
                }
            }
        }

        SpanText(
            text = "Show More",
            modifier = ShowMoreButtonStyle.toModifier()
                .display(if (showMoreVisibility) DisplayStyle.Block else DisplayStyle.None)
                .onClick {
                    onShowMore()
                }
        )
    }

}

@Composable
fun PinPreview(
    pin: PinDTO,
    selectedItems: List<String>,
    onSelect: (String) -> Unit,
    onDeselect: (String) -> Unit,
    onEdit: (PinDTO) -> Unit,
) {
    // Deriving the checkbox state from the parent-selectedItems
    val checked = selectedItems.contains(pin.id.toString())

    Tr(
        attrs = TableRowHoverStyle.toModifier()
            .onClick {
                onEdit(pin)
            }
            .toAttrs()
    ) {
        Td(SmallColumnStyle.toAttrs()) {
            Box(Modifier.fillMaxSize(),
                contentAlignment = Alignment.Center
            ) {
                CheckboxInput(
                    checked = checked,
                    attrs = Modifier
                        .onClick {
                            it.stopPropagation()
                            if (checked) {
                                onDeselect(pin.id.toString())
                            } else {
                                onSelect(pin.id.toString())
                            }
                        }
                        .size(20.px)
                        .toAttrs()
                )
            }
        }
        Td(SmallColumnStyle.toAttrs()) {
            Text(pin.id.toString())
        }
        Td(MediumColumnStyle.toAttrs()) {
            Text(pin.pin)
        }
        Td(LargeColumnStyle
            .toAttrs {
        }) {
            Text(pin.description)
        }
        Td(MediumColumnStyle.toAttrs()) {
            pin.createdAt?.let { formatDate(it) }?.let { Text(it) }
        }
    }
}